import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Component, OnInit } from '@angular/core';
import { Observable, of, Subscription } from 'rxjs';
import { RolesService } from '../services/roles.service';
import {
  IAdminUserProfileFeaturePermission,
  IRecursoDetalhe,
  IRecursoFuncionalidade,
  ISalvarPermissaoPerfil
} from '../models/recursos';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from 'src/app/shared/services/toast.service';
import { IRolePermissionArray } from '../models/role';
import { EAccountRole, ERoleAdminString } from 'src/app/shared/data/role.enum';
import { SessionService } from 'src/app/shared/services/session.service';
import { PartnerService, IPartner } from '../services/partner.service';
import { groupPermissions, IPermissionGroup } from './permission-groups';

interface IPermissionControl {
  description: string;
  control: UntypedFormGroup;
}

@Component({
  selector: 'app-role-management',
  templateUrl: './role-management.component.html',
  styleUrls: ['./role-management.component.scss']
})
export class RoleManagementComponent implements OnInit {
  ERoleAdminString = ERoleAdminString;
  currentResource = '';
  currentResourceSubscription = new Subscription();
  resources: IRecursoFuncionalidade[] = [];

  form = new UntypedFormGroup({
    description: new UntypedFormControl('', [Validators.required]),
    comments: new UntypedFormControl('', [Validators.required]),
    adminOlive: new UntypedFormControl(0),
    resource: new UntypedFormControl(null),
    partnersId: new UntypedFormControl([])
  });

  detalheRecurso!: IRecursoDetalhe;

  papelId!: string | null;

  isAdmOlive = false;

  partners: IPartner[] = [];

  permissionGroups: IPermissionGroup<IPermissionControl>[] = [];

  constructor(
    private readonly rolesService: RolesService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly toast: ToastService,
    private readonly router: Router,
    private readonly sessionService: SessionService,
    private readonly partnerService: PartnerService
  ) {}

  ngOnInit(): void {
    this.papelId = this.activatedRoute.snapshot.paramMap.get('id');

    this.isAdmOlive = this.sessionService.getSession().accountRole == EAccountRole.Admin.toString();

    if (this.papelId) {
      this.carregarDetalhesRecurso(this.papelId);
    } else {
      this.carregarRecursos();
    }

    if (this.isAdmOlive) {
      this.carregarPartners();
    }
  }

  carregarPartners(): void {
    this.partnerService.listarParceirosAtivas().subscribe((response) => {
      this.partners = response.data;
    });
  }

  convertCheckboxValue(): void {
    const adminOliveValue = this.form.get('adminOlive')?.value;
    this.form.get('adminOlive')?.setValue(adminOliveValue ? 1 : 0);
  }

  carregarDetalhesRecurso(id: string): void {
    this.rolesService.carregarDetalheRecurso(id).subscribe({
      next: (detalheRecurso) => {
        this.detalheRecurso = detalheRecurso.data;

        this.form.get('description')?.patchValue(this.detalheRecurso.description);
        this.form.get('comments')?.patchValue(this.detalheRecurso.comments);
        this.form.get('adminOlive')?.patchValue(this.detalheRecurso.adminOlive);

        const partnersId = this.detalheRecurso.partnerAdminUserProfile?.map((partner) => partner.partnerId) || [];
        this.form.get('partnersId')?.patchValue(partnersId);

        this.carregarRecursos();
      },
      error: (error) => {
        this.carregarRecursos();
      }
    });
  }

  carregarRecursos(): void {
    this.rolesService.carregarRecursos().subscribe({
      next: (recursoFuncionalidade) => {
        this.resources = recursoFuncionalidade.data;
        this.createForm();
        this.subscribeResource();
      },
      error: (error) => {
        console.error('Erro ao carregar recursos:', error);
      }
    });
  }

  salvarAcesso(): void {
    if (!!this.currentResource) {
      this.salvarPermissoes();
    }

    this.rolesService
      .editarPerfil({
        id: this.detalheRecurso.id,
        active: this.detalheRecurso.active,
        description: this.form.get('description')?.value,
        comments: this.form.get('comments')?.value,
        adminOlive: this.form.get('adminOlive')?.value,
        partnersId: this.form.get('partnersId')?.value
      })
      .subscribe((resposta) => {
        if (resposta.messages && resposta.messages.length > 0) {
          this.toast.toast(resposta.messages);
        }

        if (resposta.isSucceed) {
          this.router.navigateByUrl('/dash/papeis');
        }
      });
  }

  salvarAcessoParcial(dados: ISalvarPermissaoPerfil): void {
    this.rolesService.salvarPermissao(dados).subscribe(() => {});
  }

  subscribeResource(): void {
    this.currentResourceSubscription = this.field.resource.valueChanges.subscribe((value) => {
      if (!!this.currentResource) {
        this.salvarPermissoes();
      }

      const resourceSelected = this.resources.find((resource) => resource.id === value);
      this.currentResource = resourceSelected?.id ?? '';
      this.permissionGroups = groupPermissions(
        (this.getPermissions()?.controls ?? []).map((control) => ({
          description: control.get('description')?.value ?? '',
          control: control as UntypedFormGroup
        }))
      );
    });
  }

  salvarPermissoes(): void {
    const dados = this.form.getRawValue();
    const rolePermissionsArray: IRolePermissionArray[] = dados[this.currentResource];

    if (rolePermissionsArray) {
      const ativos = rolePermissionsArray.filter((rolePermissions) => rolePermissions.checked);

      const dados = {
        adminUserProfileId: this.papelId!,
        featureId: this.currentResource,
        featurePermissionsIds: ativos.map((ativos) => ativos.id)
      };

      this.salvarAcessoParcial(dados);
    }
  }

  get field(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }

  createForm(): void {
    this.resources.forEach((resource) => {
      this.form.addControl(
        resource.id,
        new UntypedFormArray(
          resource.featurePermissions.map((featurePermission) => {
            const permissao = this.detalheRecurso.adminUserProfileFeaturePermission.find(
              (item) => item.featurePermissionId === featurePermission.id
            );

            return new UntypedFormGroup({
              id: new UntypedFormControl(featurePermission.id),
              checked: new UntypedFormControl(!!permissao),
              adminUserProfileId: new UntypedFormControl(this.papelId),
              featureId: new UntypedFormControl(resource.id),
              description: new UntypedFormControl(featurePermission.description)
            });
          })
        )
      );
    });
  }

  getCurrentResourceObject(): Observable<IRecursoFuncionalidade> | undefined {
    const resource = this.resources.find((resource) => resource.id === this.currentResource);

    if (resource) {
      return of(resource);
    }

    return resource;
  }

  // Marcar qualquer ação marca também a listagem do mesmo grupo (ex.: Colaboradores - Export
  // marca Colaboradores - List): sem listar, a tela nem abre.
  autoSetListPermission(formGroup: UntypedFormGroup): void {
    if (!formGroup.value.checked) {
      return;
    }

    const isList = (label: string): boolean => label === 'List' || label.includes('Listar');
    const group = this.permissionGroups.find((g) => g.options.some((option) => option.item.control === formGroup));
    const listOption = group?.options.find((option) => isList(option.label));

    if (listOption && listOption.item.control !== formGroup && !listOption.item.control.value.checked) {
      listOption.item.control.get('checked')?.patchValue(true);
    }
  }

  getPermissions(): UntypedFormArray {
    return this.form.get(this.currentResource) as UntypedFormArray;
  }

  selecionarTodos(): void {
    const permissoes = this.getPermissions().value;

    permissoes.forEach((permissao: IRolePermissionArray) => {
      permissao.checked = true;
    });

    this.getPermissions().patchValue(permissoes);
  }
}
